import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { CreateFormLayout } from 'components/create-form/CreateFormLayout';
import { MultiFormCardHeader } from 'components/MultiFormCardHeader';
import { FORM, LAYOUT_ITEM, YUP } from 'constants/FormConstants';
import {
	TextFieldLayoutItem,
	SelectLayoutItem,
	ItemList,
	CheckboxLayoutItem,
} from 'models/InputLayoutItem';
import { DATA_LAYERS } from 'constants/DataLayers';
import TagManager from 'react-gtm-module';
import { useFormField } from 'hooks/useFormField';
import { DataLayerMarkupProps } from 'models/props/DataLayerMarkupProps';
import getYearOptions from 'utils/GetYearOptions';

interface DataLayerLoanSimpleMarkupProps extends DataLayerMarkupProps {
	isLoanSimple?: boolean;
}

const calculateDownPaymentPercent = (vehicleValue: number, vehicleYear: number): ItemList[] => {
	let percent;
	let newCarYear = new Date().getFullYear() - 4;

	if (vehicleYear <= newCarYear) {
		percent = [30, 40, 50, 60];
	} else {
		percent = [20, 30, 40, 50, 60];
	}
	let options: ItemList[];
	options = percent.map((value) => {
		const downPayment = Math.round((value * vehicleValue) / 100);
		return {
			label: `${value}% - $ ${new Intl.NumberFormat('es-CL').format(downPayment)}`,
			value: downPayment.toString(),
		};
	});

	return options;
};

export const LoanSimpleVehicleInfoForm = ({
	dataLayerKey,
	isLoanSimple = false,
}: DataLayerLoanSimpleMarkupProps) => {
	const [resetField, setResetField] = useState('');

	const baseFields = [
		{
			label: 'Valor del vehículo ',
			defaultValue: '',
			name: FORM.VEHICLE_VALUE,
			type: LAYOUT_ITEM.TYPE.INPUT,
			prefix: '$',
			mask: 'money',
			placeholder: ' Ej: 9.000.000',
			validation: yup
				.number()
				.required('Campo Requerido')
				.typeError('Campo Requerido')
				.min(3000000, 'Ingrese un valor superior a 3.000.000'),
		} as TextFieldLayoutItem,
		{
			defaultValue: '',
			label: 'Año del vehículo',
			name: FORM.VEHICLE_YEAR,
			type: LAYOUT_ITEM.TYPE.SELECT,
			options: getYearOptions(),
			validation: yup.string().required('Campo Requerido'),
		} as SelectLayoutItem,
		{
			defaultValue: '',
			label: 'Nº de cuotas',
			name: FORM.LOAN_INSTALLMENT_NUMBER,
			type: LAYOUT_ITEM.TYPE.SELECT,
			options: [
				{
					label: '24',
					value: '24',
				},
				{
					label: '36',
					value: '36',
				},
				{
					label: '48',
					value: '48',
				},
			],
			validation: yup.string().required('Campo Requerido'),
		} as SelectLayoutItem,
		{
			defaultValue: '',
			label: '% de pie',
			name: FORM.DOWN_PAYMENT,
			type: LAYOUT_ITEM.TYPE.SELECT,
			options: [],
			validation: yup.string().required('Campo Requerido'),
		} as SelectLayoutItem,
	];

	const initialFields = isLoanSimple
		? [
				...baseFields,
				{
					defaultValue: false,
					label: '',
					name: FORM.CONSENT_CHECKED,
					type: LAYOUT_ITEM.TYPE.CONSENT_CHECKBOX,
					validation: yup.boolean().required(YUP.CONSENT_ACCEPT).oneOf([true], YUP.CONSENT_ACCEPT),
				} as CheckboxLayoutItem,
			]
		: baseFields;

	const { fields, setFields } = useFormField(initialFields);

	const setDownPayment = (vehicleValue: number, vehicleYear: number) => {
		let newFields = fields;
		newFields[3].options =
			vehicleValue >= 3000000 ? calculateDownPaymentPercent(vehicleValue, vehicleYear) : [];
		setFields(newFields);
		setResetField(FORM.DOWN_PAYMENT);
	};

	useEffect(
		() => {
			if (dataLayerKey) {
				TagManager.dataLayer({
					dataLayer: DATA_LAYERS.get(dataLayerKey),
				});
			}
		},
		//eslint-disable-next-line
		[]
	);

	return (
		<div>
			<MultiFormCardHeader
				title="Información del vehículo"
				body="Ingresa los datos del vehículo que te gustaría comprar."
			/>
			<CreateFormLayout
				key={fields.length}
				fields={fields}
				resetField={resetField}
				watchField={[FORM.VEHICLE_VALUE, FORM.VEHICLE_YEAR]}
				customEvent={(values) => {
					if (values.vehicleValue && values.vehicleYear) {
						setDownPayment(parseInt(values.vehicleValue), parseInt(values.vehicleYear));
					}
				}}
			/>
		</div>
	);
};
